import {useEffect, useState} from 'react';
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Grid,
  HStack,
  IconButton,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalOverlay,
  Select,
  Switch,
  Text,
  Textarea,
  VStack,
  useToast,
} from '@chakra-ui/react';
import {useAuth} from '../contexts/AuthContext';
import {useUser} from '../contexts/UserContext';
import {scribePreferences} from '../services/scribePreferencesService';
import {TablerCheck, TablerX} from '../icons';
import {CREATE_SESSION_SECTION_LABEL_PROPS} from './planner/createSessionSectionLayout';
import {
  combinePhoneExtension,
  formatCreateSessionPhoneNumber,
  splitPhoneExtension,
} from './planner/createSessionFormUtils';
import LanguageMultiSelect from './common/LanguageMultiSelect';
import {TITLE_OPTIONS, OTHER_TITLE} from './onboarding/OnboardingModal';
import usePlannerStyles from './planner/usePlannerStyles';

const PROFILE_CARD_PADDING = {base: 4, md: 5};
const PROFILE_CLOSE_BUTTON_SIZE = '1.5rem';
const PROFILE_CLOSE_BUTTON_TOP = {
  base: 'calc(1rem + 0.4375rem)',
  md: 'calc(1.25rem + 0.4375rem)',
};
const PROFILE_CLOSE_BUTTON_X_ALIGN_OFFSET = '0.25rem';
const PROFILE_ACTION_TEXT_BOX_PROPS = {
  minW: 0,
  flex: '1 1 25rem',
  maxW: {sm: '25rem'},
};
const PROFILE_ROLE_LABELS: Record<string, string> = {
  admin: 'Admin',
  limited: 'Limited',
  regular: 'Regular',
  superadmin: 'Super Admin',
  user: 'User',
};

// Whole-note verbosity options. Mirrors `NOTE_LENGTH_OPTIONS` in
// `common/NoteCustomizationModal` (the per-note "Customize notes" surface) so
// the account-level control in the profile stays in lockstep with the values
// the backend understands (`scribePreferencesService` coerces anything else
// back to "standard").
const NOTE_LENGTH_OPTIONS = [
  {
    value: 'detailed',
    label: 'Detailed',
    description:
      'Full clinical narrative. Surfaces every relevant detail discussed.',
  },
  {
    value: 'standard',
    label: 'Standard',
    description: 'Typical clinical concision. The default for new accounts.',
  },
  {
    value: 'shortened',
    label: 'Shortened',
    description:
      'Brief, high-signal. Bullets and fragments instead of long sentences.',
  },
];

// Same ceiling the Customize-notes modal enforces so a profile-set style and a
// per-note style can never disagree on how much text we'll persist.
const MAX_NOTE_STYLE_CHARS = 4000;

const formatProfileRole = (role: any) => {
  if (!role) return 'User';
  const normalizedRole = String(role).trim();
  if (!normalizedRole) return 'User';
  return (
    PROFILE_ROLE_LABELS[normalizedRole.toLowerCase()] ||
    normalizedRole
      .replace(/[-_]+/g, ' ')
      .split(/\s+/)
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ')
  );
};

const InfoItem = ({label, value, styles}: Record<string, any>) => (
  <Box minW={0} py={2}>
    <Text color={styles.subtleText} fontSize="sm" fontWeight="normal">
      {label}
    </Text>
    <Text
      color={styles.textColor}
      fontSize="md"
      lineHeight="1.35"
      wordBreak="break-word"
    >
      {value || 'N/A'}
    </Text>
  </Box>
);

const ProfileToggle = ({
  ariaLabel,
  isChecked,
  onChange,
  styles,
}: Record<string, any>) => (
  <Flex align="center" gap={2} flexShrink={0} alignSelf="flex-start">
    <Text
      color={isChecked ? styles.subtleText : styles.textColor}
      fontSize="sm"
      minW="2em"
      textAlign="right"
    >
      Off
    </Text>
    <Switch
      aria-label={ariaLabel}
      colorScheme="gray"
      size="lg"
      isChecked={isChecked}
      onChange={event => onChange(event.target.checked)}
    />
    <Text
      color={isChecked ? styles.textColor : styles.subtleText}
      fontSize="sm"
      minW="1.75em"
    >
      On
    </Text>
  </Flex>
);

const ProfileSection = ({
  children,
  id,
  showTopDivider = false,
  styles,
  title,
}: Record<string, any>) => (
  <Box
    as="section"
    data-testid={`profile-${id}-section`}
    aria-labelledby={`profile-${id}-heading`}
    borderTopWidth={showTopDivider ? '1px' : 0}
    borderColor={styles.listRowDividerColor || styles.rowBorderColor}
    pt={showTopDivider ? 4 : 0}
  >
    <Flex
      gap={{base: 2, md: 3}}
      align={{base: 'stretch', md: 'flex-start'}}
      direction={{base: 'column', md: 'row'}}
    >
      <Text
        as="h3"
        id={`profile-${id}-heading`}
        data-testid={`profile-${id}-section-label`}
        color={styles.textColor}
        {...CREATE_SESSION_SECTION_LABEL_PROPS}
        alignItems={{md: 'flex-start'}}
        minH="auto"
        pt={2}
      >
        {title}
      </Text>
      <Box flex="1" minW={0}>
        {children}
      </Box>
    </Flex>
  </Box>
);

// Segmented "Detailed / Standard / Shortened" control. Mirrors the radiogroup
// in `NoteCustomizationModal` but painted with the planner palette so it reads
// as part of the profile rather than the indigo scribe surface.
const NoteLengthSelector = ({value, onChange, styles}: Record<string, any>) => (
  <HStack
    role="radiogroup"
    aria-labelledby="profile-note-length-label"
    spacing={2}
    align="stretch"
    flexWrap={{base: 'wrap', sm: 'nowrap'}}
  >
    {NOTE_LENGTH_OPTIONS.map(option => {
      const selected = value === option.value;
      return (
        <Button
          key={option.value}
          role="radio"
          aria-checked={selected}
          onClick={() => onChange(option.value)}
          flex={{base: '1 1 100%', sm: 1}}
          minH="44px"
          size="md"
          fontSize="sm"
          fontWeight="normal"
          variant="outline"
          borderWidth="1px"
          borderRadius="lg"
          borderColor={
            selected ? styles.listRowSelectedBorderColor : styles.borderColor
          }
          bg={selected ? styles.listRowSelectedBg : 'transparent'}
          color={selected ? styles.textColor : styles.subtleText}
          _hover={{
            borderColor: styles.listRowSelectedBorderColor,
            bg: selected ? styles.listRowSelectedBg : styles.hoverBg,
          }}
          _focusVisible={styles.focusOutlineStyles}
          leftIcon={
            selected ? <TablerCheck size={14} aria-hidden="true" /> : undefined
          }
        >
          {option.label}
        </Button>
      );
    })}
  </HStack>
);

const ProfileModal = ({isOpen, onClose}: Record<string, any>) => {
  const {currentUser, resetPassword} = useAuth();
  const {userData, mockEhrMode, setMockEhrMode, completeOnboarding} = useUser();
  const toast = useToast();
  const styles = usePlannerStyles();
  const [resettingPassword, setResettingPassword] = useState(false);
  const [savingMockEhrMode, setSavingMockEhrMode] = useState(false);
  const [phoneInput, setPhoneInput] = useState(
    formatCreateSessionPhoneNumber(userData?.providerPhone || ''),
  );
  const [firstName, setFirstName] = useState(userData?.firstName || '');
  const [lastName, setLastName] = useState(userData?.lastName || '');
  const initialTitleIsPreset = TITLE_OPTIONS.includes(userData?.title || '');
  const [titleSelection, setTitleSelection] = useState(
    userData?.title
      ? initialTitleIsPreset
        ? userData.title
        : OTHER_TITLE
      : '',
  );
  const [customTitle, setCustomTitle] = useState(
    userData?.title && !initialTitleIsPreset ? userData.title : '',
  );
  const [spokenLanguages, setSpokenLanguages] = useState<string[]>(
    userData?.spokenLanguages || [],
  );
  const canUseInternalSettings = userData?.role === 'superadmin';
  // Note-style preferences live in `scribePreferencesService` (localStorage +
  // Firestore `User/{uid}.scribePreferences`), the same store the per-note
  // "Customize notes" modal writes to. They're EHR-agnostic, so this section
  // behaves identically for EHR-linked and standalone accounts. We only hide
  // it when the account can't generate notes at all (scribe disabled).
  const showNoteStyle = !userData?.scribeDisabled;
  const [noteLength, setNoteLength] = useState('standard');
  const [noteStyle, setNoteStyle] = useState('');
  const [plainLanguage, setPlainLanguage] = useState(false);
  const noteLengthDescription =
    NOTE_LENGTH_OPTIONS.find(option => option.value === noteLength)
      ?.description || '';

  // Keep the editable fields in sync if the saved values change elsewhere
  // (e.g. phone remembered after placing a call) or when reopening the modal.
  useEffect(() => {
    setPhoneInput(
      formatCreateSessionPhoneNumber(userData?.providerPhone || ''),
    );
    setFirstName(userData?.firstName || '');
    setLastName(userData?.lastName || '');
    const preset = TITLE_OPTIONS.includes(userData?.title || '');
    setTitleSelection(
      userData?.title ? (preset ? userData.title : OTHER_TITLE) : '',
    );
    setCustomTitle(userData?.title && !preset ? userData.title : '');
    setSpokenLanguages(userData?.spokenLanguages || []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    userData?.providerPhone,
    userData?.firstName,
    userData?.lastName,
    userData?.title,
    isOpen,
  ]);

  // Persists the current editable profile state. Name is required, so a blank
  // name is ignored (the field stays editable but won't overwrite the saved
  // value with an empty string).
  const persistProfile = (overrides: Record<string, any> = {}) => {
    const nextFirst = (overrides.firstName ?? firstName).trim();
    const nextLast = (overrides.lastName ?? lastName).trim();
    if (!nextFirst || !nextLast) return;
    const selection = overrides.titleSelection ?? titleSelection;
    const custom = overrides.customTitle ?? customTitle;
    const resolvedTitle = selection === OTHER_TITLE ? custom.trim() : selection;
    void completeOnboarding({
      firstName: nextFirst,
      lastName: nextLast,
      title: resolvedTitle,
      providerPhone: (overrides.phone ?? phoneInput).trim(),
      spokenLanguages: overrides.spokenLanguages ?? spokenLanguages,
    }).catch(() => {
      toast({
        title: 'Could not save your profile',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    });
  };

  const handlePhoneBlur = () => {
    const next = phoneInput.trim();
    if (next === (userData?.providerPhone || '').trim()) return;
    persistProfile({phone: next});
  };

  const handleLanguagesChange = (languages: string[]) => {
    setSpokenLanguages(languages);
    persistProfile({spokenLanguages: languages});
  };

  // Hydrate the note-style controls from the local pref cache whenever the
  // modal opens. `UserContext` already pulls Firestore prefs into localStorage
  // on login, so reading the synchronous local layer here is enough — and
  // re-reading on each open picks up any change made via the per-note
  // Customize modal in between.
  useEffect(() => {
    if (!isOpen) return;
    setNoteLength(scribePreferences.getNoteLength());
    setNoteStyle(scribePreferences.getCustomInstructions());
    setPlainLanguage(scribePreferences.getFormatting().plainLanguage);
  }, [isOpen]);

  // Fire-and-forget cross-device sync. localStorage is already written by the
  // setters below, so the UI is correct even if this never resolves; matches
  // the Customize-notes modal's behavior.
  const syncNoteStyle = () => {
    if (!userData?.userId) return;
    scribePreferences.syncToFirebase(userData.userId).catch(() => {});
  };

  const handleNoteLengthChange = (value: string) => {
    setNoteLength(value);
    scribePreferences.setNoteLength(value);
    syncNoteStyle();
  };

  const handleNoteStyleBlur = () => {
    const trimmed = noteStyle.slice(0, MAX_NOTE_STYLE_CHARS);
    if (trimmed === scribePreferences.getCustomInstructions()) return;
    // Profile-set instructions are an account-level preference, so they're
    // always remembered. Clear any one-off (this-note-only) text so the saved
    // value is the single source of truth the backend reads going forward.
    scribePreferences.setRememberInstructions(true);
    scribePreferences.setCustomInstructions(trimmed);
    scribePreferences.clearEphemeralInstructions();
    syncNoteStyle();
  };

  const handlePlainLanguageChange = (value: boolean) => {
    setPlainLanguage(value);
    scribePreferences.setFormatting({plainLanguage: value});
    syncNoteStyle();
  };

  const handlePasswordReset = async () => {
    if (!currentUser?.email || !resetPassword) return;
    setResettingPassword(true);
    try {
      await resetPassword(currentUser.email);
      toast({
        title: 'Password reset email sent',
        description: `Check ${currentUser.email} for a link to reset your password.`,
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
    } catch (err) {
      toast({
        title: 'Could not send reset email',
        description: err instanceof Error ? err.message : 'Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setResettingPassword(false);
    }
  };

  const handleMockEhrModeChange = async (enabled: boolean) => {
    setSavingMockEhrMode(true);
    try {
      await setMockEhrMode(enabled);
    } catch (error) {
      toast({
        title: 'Could not save Mock EHR Mode',
        description:
          error instanceof Error
            ? error.message
            : 'The previous setting was restored.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setSavingMockEhrMode(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      isCentered
      size="xl"
      scrollBehavior="inside"
    >
      <ModalOverlay />
      <ModalContent
        data-testid="profile-modal"
        bg="transparent"
        boxShadow="none"
        w="calc(100% - 2rem)"
        maxW="750px"
        mx={4}
      >
        <IconButton
          aria-label="Close"
          icon={<TablerX size={16} />}
          position="absolute"
          top={PROFILE_CLOSE_BUTTON_TOP}
          right={PROFILE_CARD_PADDING}
          transform={`translateX(${PROFILE_CLOSE_BUTTON_X_ALIGN_OFFSET})`}
          zIndex={1}
          boxSize={PROFILE_CLOSE_BUTTON_SIZE}
          minW={PROFILE_CLOSE_BUTTON_SIZE}
          p={0}
          bg="transparent"
          color={styles.subtleText}
          borderWidth="0"
          borderRadius="0"
          variant="ghost"
          onClick={onClose}
          _hover={{bg: 'transparent', color: styles.textColor}}
          _active={{bg: 'transparent'}}
          _focus={{bg: 'transparent'}}
          _focusVisible={styles.focusOutlineStyles}
        />
        <ModalBody p={0}>
          <Box
            bg={styles.surfaceBg}
            borderWidth="1px"
            borderColor={styles.borderColor}
            borderRadius="xl"
            boxShadow={styles.cardShadow}
            overflow="hidden"
          >
            <VStack spacing={4} align="stretch" p={PROFILE_CARD_PADDING}>
              <Text
                as="h2"
                color={styles.textColor}
                fontSize="2xl"
                fontWeight="semibold"
                lineHeight="1.25"
                letterSpacing="0"
                mb={0}
                pt={1}
                pb={2}
                pr={10}
                noOfLines={2}
                wordBreak="break-word"
              >
                Profile
              </Text>

              <VStack align="stretch" spacing={4}>
                <ProfileSection id="info" title="Info" styles={styles}>
                  <Grid
                    templateColumns={{
                      base: '1fr',
                      md: 'repeat(2, minmax(0, 1fr))',
                    }}
                    gap={3}
                    w="100%"
                  >
                    <InfoItem
                      label="Email"
                      value={currentUser?.email}
                      styles={styles}
                    />
                    <InfoItem
                      label="Role"
                      value={formatProfileRole(userData?.role)}
                      styles={styles}
                    />
                    <InfoItem
                      label="Company"
                      value={
                        userData?.companyName || userData?.companyId || 'N/A'
                      }
                      styles={styles}
                    />
                    <InfoItem
                      label="Facility"
                      value={
                        userData?.facilityName || userData?.facilityId || 'N/A'
                      }
                      styles={styles}
                    />
                    <FormControl minW={0} py={2}>
                      <FormLabel
                        color={styles.subtleText}
                        fontSize="sm"
                        fontWeight="normal"
                        mb={1}
                      >
                        First name
                      </FormLabel>
                      <Input
                        value={firstName}
                        onChange={event => setFirstName(event.target.value)}
                        onBlur={() => persistProfile()}
                        placeholder="First name"
                        aria-label="First name"
                        borderRadius="lg"
                        borderColor={styles.borderColor}
                        _hover={{borderColor: styles.borderColor}}
                        _focusVisible={styles.inputFocusStyles}
                      />
                    </FormControl>
                    <FormControl minW={0} py={2}>
                      <FormLabel
                        color={styles.subtleText}
                        fontSize="sm"
                        fontWeight="normal"
                        mb={1}
                      >
                        Last name
                      </FormLabel>
                      <Input
                        value={lastName}
                        onChange={event => setLastName(event.target.value)}
                        onBlur={() => persistProfile()}
                        placeholder="Last name"
                        aria-label="Last name"
                        borderRadius="lg"
                        borderColor={styles.borderColor}
                        _hover={{borderColor: styles.borderColor}}
                        _focusVisible={styles.inputFocusStyles}
                      />
                    </FormControl>
                    <FormControl minW={0} py={2}>
                      <FormLabel
                        color={styles.subtleText}
                        fontSize="sm"
                        fontWeight="normal"
                        mb={1}
                      >
                        Title
                      </FormLabel>
                      <Select
                        value={titleSelection}
                        onChange={event => {
                          const next = event.target.value;
                          setTitleSelection(next);
                          if (next !== OTHER_TITLE) {
                            persistProfile({titleSelection: next});
                          }
                        }}
                        placeholder="Select title"
                        borderRadius="lg"
                        borderColor={styles.borderColor}
                      >
                        {TITLE_OPTIONS.map(option => (
                          <option key={option} value={option}>
                            {option}
                          </option>
                        ))}
                        <option value={OTHER_TITLE}>{OTHER_TITLE}</option>
                      </Select>
                    </FormControl>
                    {titleSelection === OTHER_TITLE && (
                      <FormControl minW={0} py={2}>
                        <FormLabel
                          color={styles.subtleText}
                          fontSize="sm"
                          fontWeight="normal"
                          mb={1}
                        >
                          Custom title
                        </FormLabel>
                        <Input
                          value={customTitle}
                          onChange={event => setCustomTitle(event.target.value)}
                          onBlur={() => persistProfile()}
                          placeholder="e.g. CNS"
                          aria-label="Custom title"
                          borderRadius="lg"
                          borderColor={styles.borderColor}
                          _hover={{borderColor: styles.borderColor}}
                          _focusVisible={styles.inputFocusStyles}
                        />
                      </FormControl>
                    )}
                    <FormControl minW={0} py={2}>
                      <FormLabel
                        color={styles.subtleText}
                        fontSize="sm"
                        fontWeight="normal"
                        mb={1}
                      >
                        Phone number
                      </FormLabel>
                      <HStack spacing={2} align="stretch">
                        <Input
                          flex="1"
                          value={splitPhoneExtension(phoneInput).number}
                          onChange={event =>
                            setPhoneInput(
                              combinePhoneExtension(
                                formatCreateSessionPhoneNumber(
                                  event.target.value,
                                ),
                                splitPhoneExtension(phoneInput).extension,
                              ),
                            )
                          }
                          onBlur={handlePhoneBlur}
                          placeholder="Your phone number"
                          aria-label="Phone number"
                          type="tel"
                          borderRadius="lg"
                          borderColor={styles.borderColor}
                          _hover={{borderColor: styles.borderColor}}
                          _focusVisible={styles.inputFocusStyles}
                        />
                        <Input
                          w="88px"
                          flexShrink={0}
                          value={splitPhoneExtension(phoneInput).extension}
                          onChange={event =>
                            setPhoneInput(
                              combinePhoneExtension(
                                splitPhoneExtension(phoneInput).number,
                                event.target.value,
                              ),
                            )
                          }
                          onBlur={handlePhoneBlur}
                          placeholder="Ext."
                          aria-label="Phone extension"
                          type="tel"
                          inputMode="numeric"
                          borderRadius="lg"
                          borderColor={styles.borderColor}
                          _hover={{borderColor: styles.borderColor}}
                          _focusVisible={styles.inputFocusStyles}
                        />
                      </HStack>
                      <Text fontSize="sm" color={styles.subtleText} mt={1}>
                        Prefilled when you start a phone encounter.
                      </Text>
                    </FormControl>
                  </Grid>
                  <FormControl minW={0} py={2} mt={1}>
                    <FormLabel
                      color={styles.subtleText}
                      fontSize="sm"
                      fontWeight="normal"
                      mb={1}
                    >
                      Languages you speak
                    </FormLabel>
                    <Text fontSize="sm" color={styles.subtleText} mb={2}>
                      Used to skip the interpreter when you already speak a
                      patient&apos;s language.
                    </Text>
                    <LanguageMultiSelect
                      value={spokenLanguages}
                      onChange={handleLanguagesChange}
                      ariaLabel="Search languages you speak"
                    />
                  </FormControl>
                </ProfileSection>

                {showNoteStyle ? (
                  <ProfileSection
                    id="note-style"
                    title="Note style"
                    styles={styles}
                    showTopDivider
                  >
                    <VStack align="stretch" spacing={5}>
                      <Text fontSize="sm" color={styles.subtleText}>
                        Shapes how Opalite writes your clinical notes. Saved to
                        your account and applied to every note you generate.
                      </Text>

                      <Box>
                        <Text
                          id="profile-note-length-label"
                          color={styles.textColor}
                          fontSize="md"
                          lineHeight="1.35"
                          mb={1}
                        >
                          Note length
                        </Text>
                        <Text fontSize="sm" color={styles.subtleText} mb={3}>
                          Choose how much detail the AI writes for the whole
                          note.
                        </Text>
                        <NoteLengthSelector
                          value={noteLength}
                          onChange={handleNoteLengthChange}
                          styles={styles}
                        />
                        <Text
                          fontSize="sm"
                          color={styles.subtleText}
                          mt={2}
                          aria-live="polite"
                        >
                          {noteLengthDescription}
                        </Text>
                      </Box>

                      <FormControl minW={0}>
                        <FormLabel
                          htmlFor="profile-note-style"
                          color={styles.textColor}
                          fontSize="md"
                          fontWeight="normal"
                          lineHeight="1.35"
                          mb={1}
                        >
                          Your style (optional)
                        </FormLabel>
                        <Text fontSize="sm" color={styles.subtleText} mb={2}>
                          Describe how you like your notes written in plain
                          language. Examples: &ldquo;Use short bullets and no
                          abbreviations.&rdquo; &ldquo;Always include patient
                          age in the first sentence.&rdquo;
                        </Text>
                        <Textarea
                          id="profile-note-style"
                          value={noteStyle}
                          onChange={event => setNoteStyle(event.target.value)}
                          onBlur={handleNoteStyleBlur}
                          placeholder="Optional. Tell the AI how you want your notes written."
                          aria-label="Your note style instructions"
                          rows={4}
                          resize="vertical"
                          maxLength={MAX_NOTE_STYLE_CHARS}
                          borderRadius="lg"
                          borderColor={styles.borderColor}
                          _hover={{borderColor: styles.borderColor}}
                          _focusVisible={styles.inputFocusStyles}
                        />
                      </FormControl>

                      <Flex
                        align={{base: 'stretch', sm: 'flex-start'}}
                        justify="space-between"
                        direction={{base: 'column', sm: 'row'}}
                        gap={4}
                      >
                        <Box {...PROFILE_ACTION_TEXT_BOX_PROPS}>
                          <Text
                            color={styles.textColor}
                            fontSize="md"
                            lineHeight="1.35"
                          >
                            Plain language
                          </Text>
                          <Text
                            fontSize="sm"
                            color={styles.subtleText}
                            lineHeight="1.45"
                          >
                            Write the plan and patient-facing instructions using
                            the everyday words said during the visit instead of
                            medical shorthand (e.g. &ldquo;every 4 hours as
                            needed for pain&rdquo; instead of &ldquo;Q4
                            prn&rdquo;).
                          </Text>
                        </Box>
                        <ProfileToggle
                          ariaLabel="Plain language"
                          isChecked={plainLanguage}
                          onChange={handlePlainLanguageChange}
                          styles={styles}
                        />
                      </Flex>
                    </VStack>
                  </ProfileSection>
                ) : null}

                {canUseInternalSettings ? (
                  <ProfileSection
                    id="mock-ehr"
                    title="Mock EHR Mode"
                    styles={styles}
                    showTopDivider
                  >
                    <Flex
                      align={{base: 'stretch', sm: 'flex-start'}}
                      justify="space-between"
                      direction={{base: 'column', sm: 'row'}}
                      gap={4}
                      py={2}
                    >
                      <Box {...PROFILE_ACTION_TEXT_BOX_PROPS}>
                        <Text fontSize="sm" color={styles.subtleText}>
                          Use deterministic patient, schedule, encounter,
                          transcript, and note fixtures to test EHR-linked
                          workflows. No real EHR reads or writes are made while
                          this mode is enabled.
                        </Text>
                      </Box>
                      <Box opacity={savingMockEhrMode ? 0.6 : 1}>
                        <ProfileToggle
                          ariaLabel="Mock EHR Mode"
                          isChecked={mockEhrMode}
                          onChange={handleMockEhrModeChange}
                          styles={styles}
                        />
                      </Box>
                    </Flex>
                  </ProfileSection>
                ) : null}

                <ProfileSection
                  id="actions"
                  title="Actions"
                  styles={styles}
                  showTopDivider
                >
                  <VStack align="stretch" spacing={3}>
                    <Flex
                      align={{base: 'stretch', sm: 'flex-start'}}
                      justify="space-between"
                      direction={{base: 'column', sm: 'row'}}
                      gap={3}
                      py={2}
                    >
                      <Box {...PROFILE_ACTION_TEXT_BOX_PROPS}>
                        <Text
                          color={styles.textColor}
                          fontSize="md"
                          lineHeight="1.35"
                        >
                          Password
                        </Text>
                        <Text
                          color={styles.subtleText}
                          fontSize="sm"
                          lineHeight="1.45"
                        >
                          Send a reset link to your account email
                        </Text>
                      </Box>
                      <Button
                        colorScheme="gray"
                        variant="outline"
                        onClick={handlePasswordReset}
                        isLoading={resettingPassword}
                        loadingText="Sending email"
                        alignSelf={{base: 'stretch', sm: 'flex-start'}}
                      >
                        Reset password
                      </Button>
                    </Flex>
                  </VStack>
                </ProfileSection>
              </VStack>
            </VStack>
          </Box>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default ProfileModal;
